import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["backends", "backendOptions", "initialLanguage"];
var _URLSearchParams$get;
import i18n from 'i18next';
import { I18nextMergeBackend } from './I18nextMergeBackend';
import { FallbackChainModule } from './FallbackChainModule';
import { NovaInterpolator } from './NovaInterpolator';
import { normalizeLocaleString } from './localeUtils';
let novaLanguage = (_URLSearchParams$get = new URLSearchParams(window.location.search).get('nova-language')) === null || _URLSearchParams$get === void 0 || (_URLSearchParams$get = _URLSearchParams$get.replace('_', '-') // i18next doesn't seem to recognize _ as a separator.
) === null || _URLSearchParams$get === void 0 ? void 0 : _URLSearchParams$get.toLowerCase();
if (novaLanguage === 'zh-xx') {
  novaLanguage = 'zh-CN-pseudo';
}
if (!novaLanguage) {
  novaLanguage = navigator.language;
}
export const configuredLanguage = normalizeLocaleString(novaLanguage);

/**
 * Initialize i18next with a backend. Needs to be called once during application startup.
 * @param backends An array of i18next backends to be used with the chained backend.
 * @param backendOptions An i18next options array for the backends
 * @param initialLanguage A BCP-47 locale that will be used as the initial language. This will override any user preferences.
 */
export function initI18next(_ref) {
  let {
      backends,
      backendOptions,
      initialLanguage
    } = _ref,
    config = _objectWithoutProperties(_ref, _excluded);
  const isDevMode = process.env.NODE_ENV === 'development';
  const mergeBackendOptions = {
    backends: backends.map((backend, i) => {
      var _backendOptions$i;
      return {
        instance: backend,
        options: (_backendOptions$i = backendOptions === null || backendOptions === void 0 ? void 0 : backendOptions[i]) !== null && _backendOptions$i !== void 0 ? _backendOptions$i : null
      };
    })
  };
  if (isDevMode) {
    // This would be simpler to use Array.sort but we can't guarantee sort is stable
    // in all of our supported browsers.
    const highPriorityBackends = mergeBackendOptions.backends.filter(b => b.instance.devPriority !== 'low');
    const lowPriorityBackends = mergeBackendOptions.backends.filter(b => b.instance.devPriority === 'low');
    mergeBackendOptions.backends = [...highPriorityBackends, ...lowPriorityBackends];
  }
  const defaultModules = [new I18nextMergeBackend(), new FallbackChainModule(), new NovaInterpolator()];
  return [...defaultModules, ...config.modules].reduce((i18n, module) => i18n.use(module), i18n).init({
    debug: false,
    fallbackLng: 'en',
    lng: initialLanguage !== null && initialLanguage !== void 0 ? initialLanguage : configuredLanguage,
    keySeparator: false,
    interpolation: {
      prefix: '{',
      suffix: '}',
      escapeValue: false
    },
    backend: mergeBackendOptions
  });
}
let initialized = false;
i18n.on('initialized', () => {
  initialized = true;
});
export const waitForInit = async () => {
  if (initialized) {
    return Promise.resolve();
  }
  return new Promise(resolve => {
    i18n.on('initialized', resolve);
  });
};

/**
 * Load a message bundle manually. The main use case is to start loading the bundle before it's
 * needed.
 *
 * @param bundleName Name of the bundle to load
 * @returns a Promise that resolves when loading is complete
 */
export const loadBundle = async bundleName => {
  await waitForInit();
  const loaded = i18n.hasResourceBundle(i18n.language, bundleName);
  if (!loaded) {
    await i18n.loadNamespaces(bundleName);
  }
};
/**
 * Returns a Promise that resolves to Localizer for the passed bundle. Will load the bundle if it is not already loaded.
 * @param bundleName Name of the bundle to get.
 * @returns a Promise that resolves to a Localizer, a function that will provide a localized message for a given key, optional parameter object, and for the passed bundle.
 */
export const getTextLocalizer = async bundleName => {
  await loadBundle(bundleName);
  return (key, params) => i18n.t(`${bundleName}:${bundleName}.${key}`, params);
};

/**
 * Returns a function that will provide localized message for a given key and option parameter object.
 * @param bundleName Name of the bundle to get.
 * @deprecated Use getTextLocalizer instead.
 */
export const _getTextLocalizer = bundleName => (key, params) => {
  return i18n.t(`${bundleName}:${bundleName}.${key}`, params);
};

/**
 * Imperatively sets the language of the application.
 * @param language The new language
 */
export const setLanguage = language => {
  i18n.changeLanguage(language);
};

/**
 * Imperatively sets a handler for when the language changes.
 * @param onChange Handler function to be called when the language is changed.
 * @returns A function to call to stop listening for language change events.
 */
export const onLanguageChange = onChange => {
  i18n.on('languageChanged', onChange);
  return () => {
    i18n.off('languageChanged', onChange);
  };
};
export const getLanguage = () => {
  var _i18n$language;
  return (_i18n$language = i18n.language) !== null && _i18n$language !== void 0 ? _i18n$language : configuredLanguage;
};
//# sourceMappingURL=index.js.map